import { useEffect, useMemo, useRef, useState } from 'react';
import { Avatar, Button, IconButton, SegmentedControl } from '../../ui/controls';
import { Icon } from '../chrome/icons';
import { Menu, useMenu } from '../../ui/menu';
import type { CommentPerson, CommentThread, FileComment } from '../../services/comments';
import { ago, mentionQuery } from './anchor';

/** A person's colour, steady for their id: the same face in every thread and pin. */
const COLOURS = ['#e8590c', '#7048e8', '#0c8599', '#2f9e44', '#c2255c', '#1971c2', '#e67700'];
export function personColour(id: string): string {
  let sum = 0;
  for (const char of id) sum = (sum * 31 + char.charCodeAt(0)) >>> 0;
  return COLOURS[sum % COLOURS.length]!;
}
const nameOf = (person: CommentPerson) => person.name || 'Someone';

/**
 * The Comments tab (W5-AR2, Farah's pick A): threads read and replied to beside the canvas, in
 * step with the pins on it. Open and Resolved; a thread pressed is the pin chosen, and a pin
 * pressed is its thread. Where a new pin is waiting for its words, the composer leads.
 */
export function CommentsPanel({
  threads,
  failed,
  canComment,
  chosen,
  numbers,
  draft,
  people,
  title = 'Comments',
  onChoose,
  onPostDraft,
  onCancelDraft,
  onReply,
  onResolve,
  onDelete,
  onRetry,
  onClose,
  className = '',
  style,
}: {
  threads: readonly CommentThread[] | null;
  failed: boolean;
  canComment: boolean;
  chosen: string | null;
  /** The number each open thread's pin wears. */
  numbers: ReadonlyMap<string, number>;
  /** A pin is down and waiting for its words. */
  draft: boolean;
  people: readonly CommentPerson[];
  title?: string;
  onChoose: (threadId: string | null) => void;
  onPostDraft: (body: string, mentions: string[]) => void;
  onCancelDraft: () => void;
  onReply: (threadId: string, body: string, mentions: string[]) => void;
  onResolve?: (threadId: string, resolved: boolean) => void;
  onDelete?: (threadId: string, commentId: string) => void;
  onRetry: () => void;
  onClose?: () => void;
  className?: string;
  style?: React.CSSProperties;
}) {
  const [show, setShow] = useState<'open' | 'resolved'>('open');
  const open = (threads ?? []).filter((one) => !one.resolved);
  const resolved = (threads ?? []).filter((one) => one.resolved);
  const shown = show === 'open' ? open : resolved;
  // A thread chosen on the canvas is the list's too, whichever side it is on.
  const chosenThread = threads?.find((one) => one.id === chosen) ?? null;
  useEffect(() => {
    if (chosenThread) setShow(chosenThread.resolved ? 'resolved' : 'open');
  }, [chosenThread]);
  const list = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!chosen) return;
    list.current?.querySelector(`[data-thread="${chosen}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [chosen, show]);

  return (
    <aside aria-label={title} className={`properties-panel context-first comments-panel pointer-events-auto flex flex-col overflow-hidden ${className}`} style={style}>
      {/* The Properties panel's header (SidePanel), so the two tabs of this place match. */}
      <header className="flex h-[48px] shrink-0 items-center gap-[8px] border-b border-separator pl-[14px] pr-[8px]">
        <h2 className="min-w-0 flex-1 truncate text-[13px] font-semibold text-label">{title}</h2>
        {threads && open.length > 0 ? <span className="shrink-0 text-[11px] tabular-nums text-label-3">{open.length === 1 ? '1 open' : `${open.length} open`}</span> : null}
        {onClose ? <IconButton icon="close" label={`Close ${title}`} onClick={onClose} /> : null}
      </header>
      {draft ? (
        <div className="shrink-0 border-b border-separator px-[16px] py-[12px]">
          <Composer people={people} placeholder="Add a comment" submit="Post" autoFocus onPost={onPostDraft} onCancel={onCancelDraft} />
        </div>
      ) : null}
      {resolved.length > 0 || show === 'resolved' ? (
        <div className="shrink-0 border-b border-separator px-[16px] py-[8px]">
          <SegmentedControl
            size="small"
            className="w-full"
            label="Which threads"
            value={show}
            onChange={setShow}
            segments={[
              { value: 'open', label: 'Open' },
              { value: 'resolved', label: 'Resolved' },
            ]}
          />
        </div>
      ) : null}
      <div ref={list} className="ui-scroll min-h-0 flex-1 overflow-y-auto">
        {threads === null && !failed
          ? [0, 1].map((index) => (
              <div key={index} className="flex flex-col gap-[8px] px-[16px] py-[12px]" aria-hidden>
                <span className="h-[10px] w-[40%] rounded-full bg-fill-1" />
                <span className="h-[10px] w-[85%] rounded-full bg-fill-1" />
              </div>
            ))
          : null}
        {failed && threads === null ? (
          <div className="flex items-center gap-[8px] px-[16px] py-[12px] text-[12px] text-label-2">
            <span className="flex-1">The comments could not be read.</span>
            <Button size="small" onClick={onRetry}>
              Try again
            </Button>
          </div>
        ) : null}
        {threads && shown.length === 0 && !draft ? (
          <p className="px-[16px] py-[16px] text-[12px] leading-[16px] text-label-2">{show === 'resolved' ? 'Nothing resolved yet.' : resolved.length > 0 ? 'No open comments.' : canComment ? 'No comments yet. Press C, then click where you want to say something.' : 'No comments yet.'}</p>
        ) : null}
        {shown.map((thread) => (
          <Thread
            key={thread.id}
            thread={thread}
            number={numbers.get(thread.id)}
            chosen={thread.id === chosen}
            canComment={canComment}
            people={people}
            onChoose={() => onChoose(thread.id === chosen ? null : thread.id)}
            onReply={(body, mentions) => onReply(thread.id, body, mentions)}
            onResolve={onResolve ? () => onResolve(thread.id, !thread.resolved) : undefined}
            onDelete={onDelete ? (commentId) => onDelete(thread.id, commentId) : undefined}
          />
        ))}
      </div>
    </aside>
  );
}

function Thread({
  thread,
  number,
  chosen,
  canComment,
  people,
  onChoose,
  onReply,
  onResolve,
  onDelete,
}: {
  thread: CommentThread;
  number?: number;
  chosen: boolean;
  canComment: boolean;
  people: readonly CommentPerson[];
  onChoose: () => void;
  onReply: (body: string, mentions: string[]) => void;
  onResolve?: () => void;
  onDelete?: (commentId: string) => void;
}) {
  const replies = thread.replies.length;
  return (
    <div data-thread={thread.id} className={`group/thread relative border-b border-separator ${chosen ? 'bg-fill-1' : ''}`}>
      <button type="button" aria-expanded={chosen} onClick={onChoose} className="block w-full px-[16px] py-[12px] text-left outline-none hover:bg-fill-1 focus-visible:bg-fill-1">
        <Byline person={thread.author} when={thread.createdAt} number={chosen && canComment ? undefined : number} resolved={chosen && canComment ? false : thread.resolved} />
        <p className={`mt-[6px] whitespace-pre-wrap break-words pl-[28px] text-[12px] leading-[16px] text-label ${chosen ? '' : 'line-clamp-3'}`}>{thread.body}</p>
        {!chosen && (replies > 0 || canComment) ? (
          <span className="mt-[6px] block pl-[28px] text-[11px] font-medium text-label-2">{replies === 0 ? 'Reply' : replies === 1 ? '1 reply' : `${replies} replies`}</span>
        ) : null}
      </button>
      {chosen ? (
        <div className="px-[16px] pb-[12px]">
          {thread.replies.map((reply) => (
            <Reply key={reply.id} reply={reply} onDelete={onDelete && reply.mine ? () => onDelete(reply.id) : undefined} />
          ))}
          {canComment ? <Composer people={people} placeholder="Reply" submit="Reply" onPost={onReply} /> : null}
        </div>
      ) : null}
      {chosen && canComment && (onResolve || (onDelete && thread.mine)) ? (
        <div className="absolute right-[10px] top-[10px] flex items-center gap-[2px]">
          {onResolve ? (
            <IconButton icon="check" label={thread.resolved ? 'Open again' : 'Resolve'} selected={thread.resolved} onClick={onResolve} />
          ) : null}
          {onDelete && thread.mine ? <ThreadMore onDelete={() => onDelete(thread.id)} /> : null}
        </div>
      ) : null}
    </div>
  );
}

function ThreadMore({ onDelete }: { onDelete: () => void }) {
  const menu = useMenu();
  return (
    <>
      <IconButton ref={menu.ref} icon="more" label="More for this thread" onClick={menu.toggle} />
      <Menu open={menu.open} anchor={menu.anchor} onClose={menu.close} label="More for this thread" items={[{ id: 'delete', label: 'Delete thread', danger: true, onSelect: onDelete }]} />
    </>
  );
}

function Reply({ reply, onDelete }: { reply: FileComment; onDelete?: () => void }) {
  return (
    <div className="group pb-[10px]">
      <div className="flex items-center gap-[6px]">
        <div className="min-w-0 flex-1">
          <Byline person={reply.author} when={reply.createdAt} />
        </div>
        {onDelete ? (
          <button type="button" onClick={onDelete} className="touch-ring relative shrink-0 text-[11px] text-label-3 opacity-0 hover:text-danger focus-visible:opacity-100 group-hover:opacity-100 pointer-coarse:opacity-100">
            Delete
          </button>
        ) : null}
      </div>
      <p className="mt-[4px] whitespace-pre-wrap break-words pl-[28px] text-[12px] leading-[16px] text-label">{reply.body}</p>
    </div>
  );
}

function Byline({ person, when, number, resolved }: { person: CommentPerson; when: string; number?: number; resolved?: boolean }) {
  return (
    <span className="flex h-[20px] items-center gap-[8px] text-[12px]">
      <Avatar name={nameOf(person)} colour={personColour(person.id)} size={20} />
      <span className="min-w-0 truncate font-semibold text-label">{nameOf(person)}</span>
      <span className="shrink-0 text-[11px] text-label-3">{ago(when)}</span>
      {resolved ? (
        <span className="ml-auto shrink-0 text-[11px] text-label-3">Resolved</span>
      ) : number ? (
        <span aria-label={`Pin ${number}`} className="ml-auto shrink-0 text-[11px] tabular-nums text-label-3">
          #{number}
        </span>
      ) : null}
    </span>
  );
}

/**
 * Where words are written: Return posts, Shift-Return starts a new line, Escape cancels a new
 * pin. Typing @ lists the file's people; choosing one writes their name and tells them.
 */
export function Composer({
  people,
  placeholder,
  submit,
  autoFocus = false,
  onPost,
  onCancel,
}: {
  people: readonly CommentPerson[];
  placeholder: string;
  submit: string;
  autoFocus?: boolean;
  onPost: (body: string, mentions: string[]) => void;
  onCancel?: () => void;
}) {
  const [text, setText] = useState('');
  const [named, setNamed] = useState<{ id: string; name: string }[]>([]);
  const [caret, setCaret] = useState(0);
  const [pick, setPick] = useState(0);
  const field = useRef<HTMLTextAreaElement>(null);
  // The field grows with its words, up to about seven lines, then scrolls.
  useEffect(() => {
    const el = field.current;
    if (!el) return;
    el.style.height = '0px';
    el.style.height = `${Math.min(Math.max(el.scrollHeight, 36), 124)}px`;
  }, [text]);
  useEffect(() => {
    if (!autoFocus) return;
    const frame = requestAnimationFrame(() => field.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [autoFocus]);
  const asking = mentionQuery(text, caret);
  const offered = useMemo(() => {
    if (!asking) return [];
    const q = asking.query.toLowerCase();
    return people.filter((person) => person.name && person.name.toLowerCase().includes(q)).slice(0, 6);
  }, [asking, people]);
  const post = () => {
    const body = text.trim();
    if (!body) return;
    const mentions = named.filter((one) => text.includes(`@${one.name}`)).map((one) => one.id);
    onPost(body, [...new Set(mentions)]);
    setText('');
    setNamed([]);
  };
  const choose = (person: CommentPerson) => {
    if (!asking || !person.name) return;
    const next = `${text.slice(0, asking.start)}@${person.name} ${text.slice(caret)}`;
    setText(next);
    setNamed((now) => [...now, { id: person.id, name: person.name! }]);
    const at = asking.start + person.name.length + 2;
    requestAnimationFrame(() => {
      field.current?.setSelectionRange(at, at);
      setCaret(at);
    });
  };
  return (
    <div className="relative mt-[4px] rounded-[6px] border border-separator bg-[var(--panel-solid)] transition-colors hover:border-label-3 focus-within:border-label-2 focus-within:hover:border-label-2">
      <textarea
        ref={field}
        aria-label={placeholder}
        rows={1}
        value={text}
        placeholder={placeholder}
        onChange={(event) => {
          setText(event.target.value);
          setCaret(event.target.selectionStart ?? event.target.value.length);
          setPick(0);
        }}
        onSelect={(event) => setCaret(event.currentTarget.selectionStart ?? 0)}
        onKeyDown={(event) => {
          event.stopPropagation();
          if (offered.length > 0 && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
            event.preventDefault();
            setPick((now) => (now + (event.key === 'ArrowDown' ? 1 : -1) + offered.length) % offered.length);
            return;
          }
          if (offered.length > 0 && (event.key === 'Enter' || event.key === 'Tab')) {
            event.preventDefault();
            choose(offered[pick] ?? offered[0]!);
            return;
          }
          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            post();
          } else if (event.key === 'Escape' && onCancel) {
            event.preventDefault();
            onCancel();
          }
        }}
        // Not .field-focus: the laptop density pins every .field-focus to 24 px, which cut this
        // field to half a line (2 Nov, "commenting broke").
        className="block min-h-[36px] w-full resize-none overflow-y-auto bg-transparent px-[8px] pb-[2px] pt-[8px] text-[12px] leading-[16px] text-label outline-none placeholder:text-label-3"
      />
      {offered.length > 0 ? (
        <div role="listbox" aria-label="People" className="menu-surface glass-thick absolute left-0 right-0 top-full z-10 mt-[4px] rounded-[12px] p-[4px]">
          {offered.map((person, index) => (
            <button
              key={person.id}
              type="button"
              role="option"
              aria-selected={index === pick}
              onPointerDown={(event) => {
                event.preventDefault();
                choose(person);
              }}
              className={`flex h-[28px] w-full items-center gap-[7px] rounded-[7px] px-[8px] text-left text-[13px] ${index === pick ? 'bg-accent text-on-accent' : 'text-label'}`}
            >
              <Avatar name={nameOf(person)} colour={personColour(person.id)} size={18} />
              <span className="truncate">{person.name}</span>
            </button>
          ))}
        </div>
      ) : null}
      <div className="flex items-center justify-end gap-[6px] px-[4px] pb-[4px]">
        {onCancel ? (
          <button type="button" onClick={onCancel} className="h-[24px] rounded-[5px] px-[6px] text-[11px] text-label-2 hover:bg-fill-2 hover:text-label">
            Cancel
          </button>
        ) : null}
        {/* Figma's send: an arrow in a circle, grey until there is something to send. */}
        <button
          type="button"
          aria-label={submit}
          disabled={!text.trim()}
          onClick={post}
          className="grid size-[24px] place-items-center rounded-full bg-label text-[var(--panel-solid)] transition-colors disabled:bg-fill-1 disabled:text-label-3"
        >
          <Icon name="arrow-up" size={14} />
        </button>
      </div>
    </div>
  );
}

/** A pin on the canvas: the thread's number, a tick once resolved, a plus while it waits for words. */
export function CommentPin({ x, y, label, chosen, resolved, draft, onPress }: { x: number; y: number; label: string; chosen: boolean; resolved?: boolean; draft?: boolean; onPress?: () => void }) {
  return (
    <button
      type="button"
      aria-label={draft ? 'New comment here' : resolved ? `Resolved comment ${label}` : `Comment ${label}`}
      aria-pressed={chosen}
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => {
        event.stopPropagation();
        onPress?.();
      }}
      className={`comment-pin pointer-events-auto absolute grid size-[26px] place-items-center rounded-[13px_13px_13px_3px] text-[11px] font-semibold tabular-nums text-white shadow-[0_2px_8px_rgba(0,0,0,0.25)] transition-transform ${
        draft ? 'bg-label-2' : resolved ? 'bg-[#2f9e44]' : 'bg-accent'
      } ${chosen ? 'scale-[1.15] ring-2 ring-white' : ''}`}
      style={{ left: x, top: y - 26 }}
    >
      {draft ? '+' : resolved ? '✓' : label}
    </button>
  );
}
